import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import { useCompaniesStore } from "../../shared/store/companies.store";
import { CompaniesService } from "../../shared/services/companies.service";
import {
  ButtonLarge,
  ButtonLink,
  CustomInput,
  CustomTextArea,
} from "../../shared/uikit";

export const CompanyEditPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const companyId = Number(params.id);
  const store = useCompaniesStore();
  const company = store.companies.find((c) => c.id === companyId);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Company not found
  if (!company) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>Edit Company</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🔍</p>
          <p style={{ color: "var(--second-color)" }}>
            Company not found (ID: {companyId})
          </p>
        </div>
        <ButtonLarge
          title="← Back to Profile"
          onClick={() => navigate(getPath(ConfigRoutes.PROFILE))}
          mode="black"
          Accent={true}
        />
      </div>
    );
  }

  // Pre-filled form fields
  const [name, setName] = useState(company.name);
  const [logo, setLogo] = useState(company.logo || "");
  const [description, setDescription] = useState(company.description || "");
  const [address, setAddress] = useState(company.address || "");
  const [director, setDirector] = useState(company.director || "");
  const [phone, setPhone] = useState(company.telephone || "");
  const [email, setEmail] = useState(company.email || "");

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = "Company name is required";
    if (!description.trim()) errors.description = "Description is required";
    if (!address.trim()) errors.address = "Address is required";
    if (!director.trim()) errors.director = "Director is required";
    if (!phone.trim()) errors.phone = "Phone is required";
    if (!email.trim()) errors.email = "Email is required";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaveError(null);
    setSaving(true);
    try {
      const result = await CompaniesService.updateCompany({
        companyId: company.id,
        name: name.trim(),
        logo: logo.trim() || undefined,
        description: description.trim(),
        address: address.trim(),
        director: director.trim(),
        telephone: phone.trim(),
        email: email.trim(),
      });
      // Update the company in the store
      const updatedCompanies = store.companies.map((c) =>
        c.id === result.id ? result : c,
      );
      store.setCompanies(updatedCompanies);
      // Navigate back to detail page
      navigate(getPath(ConfigRoutes.COMPANY_DETAIL, { id: company.id }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save company";
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "1rem" }}>
      <h2>Edit Company</h2>

      <p style={{ color: "var(--second-color)", marginTop: "0.5rem" }}>
        Update company information below. All fields are required unless marked
        optional.
      </p>

      {/* Company Name */}
      <div style={{ marginTop: "1rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Company Name *
        </label>
        <CustomInput
          placeholder="Company name"
          value={name}
          onChangeHandler={(e) => setName(e.target.value)}
          inputMode="text"
        />
        {validationErrors.name && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.name}
          </span>
        )}
      </div>

      {/* Logo URL */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Logo URL
        </label>
        <CustomInput
          placeholder="https://example.com/logo.png (optional)"
          value={logo}
          onChangeHandler={(e) => setLogo(e.target.value)}
          inputMode="url"
        />
      </div>

      {/* Description */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Description *
        </label>
        <CustomTextArea
          value={description}
          placeholder="Company description"
          onChange={(e) => setDescription(e.target.value)}
        />
        {validationErrors.description && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.description}
          </span>
        )}
      </div>

      {/* Address */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Address *
        </label>
        <CustomInput
          placeholder="Company address"
          value={address}
          onChangeHandler={(e) => setAddress(e.target.value)}
          inputMode="text"
        />
        {validationErrors.address && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.address}
          </span>
        )}
      </div>

      {/* Director */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Director *
        </label>
        <CustomInput
          placeholder="Full name of director"
          value={director}
          onChangeHandler={(e) => setDirector(e.target.value)}
          inputMode="text"
        />
        {validationErrors.director && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.director}
          </span>
        )}
      </div>

      {/* Phone */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Phone *
        </label>
        <CustomInput
          placeholder="+7 (999) 123-45-67"
          value={phone}
          onChangeHandler={(e) => setPhone(e.target.value)}
          inputMode="tel"
        />
        {validationErrors.phone && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.phone}
          </span>
        )}
      </div>

      {/* Email */}
      <div style={{ marginTop: "0.75rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}
        >
          Email *
        </label>
        <CustomInput
          placeholder="company@example.com"
          value={email}
          onChangeHandler={(e) => setEmail(e.target.value)}
          inputMode="email"
        />
        {validationErrors.email && (
          <span style={{ color: "var(--color-danger)", fontSize: "12px" }}>
            {validationErrors.email}
          </span>
        )}
      </div>

      {/* Save error */}
      {saveError && (
        <p
          style={{
            color: "var(--color-danger)",
            fontSize: "14px",
            marginTop: "0.5rem",
          }}
        >
          ❌ {saveError}
        </p>
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
        <ButtonLarge
          title="Cancel"
          onClick={() =>
            navigate(getPath(ConfigRoutes.COMPANY_DETAIL, { id: company.id }))
          }
          mode="white"
          isSmall={true}
        />
        <ButtonLarge
          title={saving ? "⏳ Saving..." : "💾 Save"}
          onClick={handleSave}
          mode="black"
          isSmall={true}
        />
      </div>

      {/* Navigation */}
      <ButtonLink
        title="← Back to Company Detail"
        onClick={() =>
          navigate(getPath(ConfigRoutes.COMPANY_DETAIL, { id: company.id }))
        }
      />
    </div>
  );
};
